/**
 * Ad Copy Generation Component
 *
 * This component provides an AI-powered ad copy generation tool.
 * It allows users to input details about their business and funnel,
 * then generates targeted advertising copy based on those inputs.
 *
 * Features:
 * - Automatic data pre-filling from funnel information
 * - Category selection with training examples
 * - Customizable tone and style options
 * - Emoji inclusion options
 * - Special instructions support
 * - AI-generated ad copy results
 * - Copy editing and refinement
 *
 * The component leverages AI prompt templates to create effective
 * advertising copy that matches the business and funnel type.
 *
 * @module components/ad-copy/ad-copy
 */

import {
  Col,
  Row,
  Form,
  Button,
  Tooltip,
  Collapse,
  OverlayTrigger
} from "react-bootstrap";
import { useState } from "react";
import styled from "styled-components";
import { useOutletContext } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import {
  TONALITY_TYPES,
  FUNNEL_TYPE_FILTER,
  EMOJI_SELCECT_LABELS
} from "../strings";
import AdCopyResults from "./ad-copy-results";
import EditPromptModal from "./edit-prompt-modal";
import { showError } from "../../services/toast-service";
import PreventReload from "../funnel-form/prevent-reload";
import TextField from "../reusable-form-fields/text-field";
import RadioInput from "../reusable-form-fields/input-radio";
import { useOptionsFetch } from "../../hooks/useOptionsFetch";
import { FontSizeTwelve, CustomCard } from "../common-styling";
import { generateInstruction } from "../../ad-copy-prompt/prompt1";
import { AdCopyScehma } from "../../services/validation-schema";
import AsyncSelectField from "../reusable-form-fields/async-select";
import TextArea from "../../components/reusable-form-fields/text-area";
import { useTranslation } from "react-i18next";

/**
 * Styled badge component that indicates data completeness
 * Shows a small green circle in the top-right corner of the card
 * when category training examples are complete
 */
const CustomBadge = styled.div`
  top: -10px;
  right: -8px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  position: absolute;
  background: #1b8754; /* Success green color */
  cursor: pointer;
`;

/**
 * Ad Copy generation component
 *
 * @returns {JSX.Element} Ad copy generation form and results
 */
const AdCopy = () => {
  const { t } = useTranslation();

  // Get funnel data from parent component context
  const { funnel } = useOutletContext();

  /**
   * Builds the funnel URL from subdomain and domain
   *
   * @returns {string} Complete funnel URL or empty string if incomplete
   */
  const getFunnelURL = () => {
    let funnelUrl = "";
    if (funnel.subDomain && funnel.domain) {
      funnelUrl = `https://${funnel.subDomain}.${funnel.domain}`;
    }
    return funnelUrl;
  };
  /**
   * Initialize form with React Hook Form
   * Pre-fill values from funnel data
   */
  const methods = useForm({
    defaultValues: {
      companyName: funnel.companyInfo.companyName, // Company name from funnel
      category: { ...funnel.category }, // Category from funnel
      funnelUrl: getFunnelURL(), // Generated funnel URL
      type: funnel.general.funnelType, // Funnel type (lead, sale, etc.)
      emojis: EMOJI_SELCECT_LABELS.Ja, // Default to using emojis
      tonality: TONALITY_TYPES.SERIOUS, // Default to serious tone
      specialInstruction: "", // No special instructions by default
      speech: "du" // Informal speech (German "du" form)
    },
    resolver: yupResolver(AdCopyScehma) // Form validation schema
  });

  // Destructure form methods for use in the component
  const {
    register, // Register form fields
    handleSubmit, // Form submission handler
    control, // Form control for complex inputs
    formState: { errors }, // Validation errors
    watch // Watch form values for reactive updates
  } = methods;

  // Watch tonality value for conditional rendering
  const tonalityValue = watch("tonality");

  // State for storing generated ad copy results
  const [adCopyResultsData, setAdCopyResultsData] = useState({
    show: false, // Whether to show results section
    results: [] // Array of generated ad copy texts
  });

  // Check if training examples exist in the selected category
  const { trainingExample1, trainingExample2, trainingExample3 } =
    funnel.category;

  // Determine if category has any training examples
  const initialDataCondition = Boolean(
    trainingExample1 || trainingExample2 || trainingExample3
  );

  // State to track if category has complete training data
  const [isDataCompleted, setIsDataCompleted] = useState(initialDataCondition);

  // State for storing prompt modal content
  const [modalContent, setModalContent] = useState([]);
  /**
   * Handle form submission
   * Generates AI prompt instructions based on form values
   *
   * @param {Object} formValues - Form values from React Hook Form
   */
  const onSubmit = (formValues) => {
    // Reset results when generating new copy
    setAdCopyResultsData({ results: [], show: false });

    // Extract training examples from category
    const { trainingExample1, trainingExample2, trainingExample3 } =
      formValues.category;

    // Extract other form values
    const {
      category: { name }, // Category name
      companyName, // Company name
      funnelUrl, // Funnel URL
      type, // Funnel type
      emojis, // Whether to use emojis
      tonality, // Tone of voice
      specialInstruction, // Any special instructions
      speech // Speech form (du/Sie in German)
    } = formValues;

    // Determine if this is a lead generation funnel
    const isLeadFunnel = type === FUNNEL_TYPE_FILTER.LEAD ? true : false;

    // Generate AI prompt instructions
    const messages = generateInstruction({
      speech,
      productName: name,
      companyName,
      funnelURL: funnelUrl,
      isLeadFunnel,
      specialInstruction,
      shouldUseEmoji: emojis,
      tonality,
      example1: trainingExample1,
      example2: trainingExample2,
      example3: trainingExample3
    });

    // Set the modal content to show the generated prompt
    setModalContent(messages);
  };

  const onCreatePrompt = (data) => {
    try {
      if (!data.data?.length) throw Error("No Data");
      const serverResponse = data.data[0].message.content;
      // curly braces regex
      const regexsForDataExtraction = /\{\{\{([\s\S]*?)\}\}\}/g;
      const results = [];
      let match;
      while ((match = regexsForDataExtraction.exec(serverResponse)) !== null) {
        results.push(match[1].trim()); // Push the matched text (without curly braces) into the array
      }
      setAdCopyResultsData({ show: true, results });
      setModalContent([]);
    } catch (error) {
      showError(error.response.data.message);
      console.log(error);
    }
  };
  const { handleCategoryOptions } = useOptionsFetch();
  const handleCategorySelect = (value) => {
    const { trainingExample1, trainingExample2, trainingExample3 } = value;
    const dataCondition = Boolean(
      trainingExample1 || trainingExample2 || trainingExample3
    );
    setIsDataCompleted(dataCondition);
  };

  return (
    <>
      <FormProvider {...methods}>
        <PreventReload />
        <form onSubmit={handleSubmit(onSubmit)}>
          <CustomCard className="position-relative mt-4">
            {isDataCompleted && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip">{t("adCopy.dataCompleted")}</Tooltip>
                }
              >
                <CustomBadge />
              </OverlayTrigger>
            )}
            <div className="p-4">
              <Row className="mb-4">
                <Col xs={12} md={6}>
                  <TextField
                    type="text"
                    name="companyName"
                    register={register}
                    label={t("common.company")}
                    errors={errors?.companyName}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>
                      {t("common.product")} / {t("job")}
                    </Form.Label>
                    <AsyncSelectField
                      control={control}
                      name="category"
                      fetchData={handleCategoryOptions}
                      onSelect={handleCategorySelect}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <TextField
                    type="text"
                    name="funnelUrl"
                    register={register}
                    label="Funnel-URL"
                    errors={errors?.funnelUrl}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="type">
                    <Form.Label>{t("common.type")}</Form.Label>
                    <Form.Select {...register("type")}>
                      <option>{FUNNEL_TYPE_FILTER.LEAD}</option>
                      <option>{FUNNEL_TYPE_FILTER.EMPLOYEE}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3" controlId="emojis">
                    <Form.Label>{t("adCopy.emojis")}</Form.Label>
                    <Form.Select {...register("emojis")}>
                      {Object.values(EMOJI_SELCECT_LABELS).map(
                        (value, index) => (
                          <option key={index}>{value}</option>
                        )
                      )}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="tonalität">
                    <Form.Label>{t("tonality")}</Form.Label>
                    <Form.Select {...register("tonality")}>
                      {Object.values(TONALITY_TYPES).map((value, index) => (
                        <option value={value} key={index}>
                          {t(`tonalities.${value}.title`)}
                        </option>
                      ))}
                    </Form.Select>
                    <FontSizeTwelve className="text-muted my-2">
                      {t(`tonalities.${tonalityValue}.description`)}
                    </FontSizeTwelve>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <TextArea
                    label={t("specialInstructions")}
                    name="specialInstruction"
                    register={register}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <RadioInput
                    label={t("speech")}
                    firstOptLabel="Du" // formal
                    secondOptLabel="Sie" // Informal
                    name="speech" // Translation of Ansprache, formal or informal
                    firstValue={"du"}
                    firstValueId={"du"}
                    secondValue={"sie"}
                    secondValueId={"sie"}
                    register={register}
                  />
                </Col>
              </Row>
            </div>
          </CustomCard>
          <div className="d-flex justify-content-end my-3">
            <Button type="submit">
              <FontAwesomeIcon icon={faWandMagicSparkles} className="me-1" />
              {adCopyResultsData.show && adCopyResultsData.results.length === 3
                ? "Re-Generate"
                : t("common.generate")}{" "}
              {t("adCopy.title")}
            </Button>
          </div>
        </form>
      </FormProvider>
      <Collapse in={adCopyResultsData.show}>
        <div>
          <AdCopyResults results={adCopyResultsData.results} />
        </div>
      </Collapse>
      {modalContent.length > 0 && (
        <EditPromptModal
          closeModal={() => setModalContent([])}
          onCreatePrompt={onCreatePrompt}
          content={modalContent}
        />
      )}
    </>
  );
};

export default AdCopy;
